import React, { createContext, useState, ReactNode, useRef } from 'react';
import { getDefaultDropdownObj } from 'utils/utils';
import { beaconAssignmentOptionsArray } from '../components/RegisteredBeaconSearch/menuOptions';
import { BeaconSelectOptionType } from '../types';

import { LocalizedBeaconType, UnregisteredBeaconType } from 'types/BeaconType';

interface BeaconManagementContextI {
  setFilteredData: (arg: any) => void;
  searchTerm: string;
  setSearchTerm: (arg: string) => void;
  setIsSearching: (arg: boolean) => void;
  isSearching: boolean;
  isSorting: boolean;
  isRefreshing: boolean;
  setIsRefreshing: (arg: boolean) => void;
  setIsSorting: (arg: boolean) => void;
  isSearchingAll: boolean;
  searchResults: LocalizedBeaconType[];
  setSearchResults: (arg: LocalizedBeaconType[]) => void;
  setIsSearchingAll: (arg: boolean) => void;
  searchInProgress: boolean;
  setSearchInProgress: (arg: boolean) => void;
  assetAssignOptionSelectedObj: BeaconSelectOptionType;
  setAssetAssignOptionSelectedObj: (arg: BeaconSelectOptionType) => void;
  defaultDropdownObj: BeaconSelectOptionType;
  selectedSearchMenuOptionLabel: string;
  selectedSearchMenuOptionValue: string;
  setSelectedSearchMenuOptionLabel: (arg: string) => void;
  setSelectedSearchMenuOptionValue: (arg: string) => void;
  nextToken: string;
  setNextToken: (arg: string) => void;
  nextIsLoading: boolean;
  setNextIsLoading: (arg: boolean) => void;
  activeSortField: string;
  setActiveSortField: (arg: string) => void;
  activeSortDirection: string;
  setActiveSortDirection: (arg: string) => void;
  assignOptionHasNoResults: boolean;
  setAssignOptionHasNoResults: (arg: boolean) => void;
  filteredData: LocalizedBeaconType[];
  toggledClearRows: boolean;
  setToggleClearRows: (arg: boolean) => void;
  selectedRows: UnregisteredBeaconType[];
  setSelectedRows: (arg: UnregisteredBeaconType[]) => void;
  selectedBeaconDialogIsOpen: boolean;
  setSelectedBeaconDialogIsOpen: (arg: boolean) => void;
  confirmationModalOpen: boolean;
  setConfirmationModalOpen: (arg: boolean) => void;
  paginationIndex: number;
  setPaginationIndex: (arg: number) => void;
  assetTotal: number;
  setAssetTotal: (arg: number) => void;
  cachedFilteredDataRef: any;
  nextTokenCachedRef: any;
  assetTotalRef: any;
  paginationIndexRef: any;

  resetToCachedRefs: () => void;
}
export const BeaconManagementContext = createContext({} as BeaconManagementContextI);

type BeaconManagementProviderProps = { children: ReactNode };

const BeaconManagementProvider = ({ children }: BeaconManagementProviderProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ==================================
  // registered beacons only state -------
  // ==================================

  // search bar variables
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSearchMenuOptionLabel, setSelectedSearchMenuOptionLabel] = useState('');
  const [selectedSearchMenuOptionValue, setSelectedSearchMenuOptionValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchingAll, setIsSearchingAll] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [searchResults, setSearchResults] = useState<LocalizedBeaconType[]>([]);
  // assignment dropdown  variables
  const defaultDropdownObj = getDefaultDropdownObj(beaconAssignmentOptionsArray);
  const [assetAssignOptionSelectedObj, setAssetAssignOptionSelectedObj] =
    useState(defaultDropdownObj);
  const [assignOptionHasNoResults, setAssignOptionHasNoResults] = useState(false);
  // sort param variables
  const [activeSortField, setActiveSortField] = useState('beacon_id'); //default sort field
  const [activeSortDirection, setActiveSortDirection] = useState('asc'); // default sort order.
  const [isSorting, setIsSorting] = useState(false);
  // next pagination variables
  const [nextToken, setNextToken] = useState('');
  const [nextIsLoading, setNextIsLoading] = useState(false);
  const [paginationIndex, setPaginationIndex] = useState(0); // keep track of pagination clicks
  const [assetTotal, setAssetTotal] = useState(0);
  // table component variables
  const [filteredData, setFilteredData] = useState<LocalizedBeaconType[]>([]);
  const [toggledClearRows, setToggleClearRows] = useState(false);
  const [selectedRows, setSelectedRows] = useState<UnregisteredBeaconType[]>([]);
  // modal / dialog variables
  const [selectedBeaconDialogIsOpen, setSelectedBeaconDialogIsOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  //refs to keep track of state if user cancels search.
  const cachedFilteredDataRef = useRef([]);
  const nextTokenCachedRef = useRef('');
  const assetTotalRef = useRef(0);
  const paginationIndexRef = useRef(0);

  function resetToCachedRefs(): void {
    setFilteredData(cachedFilteredDataRef.current);
    setNextToken(nextTokenCachedRef.current);
    setAssetTotal(assetTotalRef.current);
    setPaginationIndex(paginationIndexRef.current);
  }

  return (
    <BeaconManagementContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        selectedSearchMenuOptionLabel,
        setSelectedSearchMenuOptionLabel,
        selectedSearchMenuOptionValue,
        setSelectedSearchMenuOptionValue,
        isSearching,
        setIsSearching,
        isSearchingAll,
        setIsSearchingAll,
        searchInProgress,
        setSearchInProgress,
        isRefreshing,
        setIsRefreshing,
        searchResults,
        setSearchResults,
        assetAssignOptionSelectedObj,
        setAssetAssignOptionSelectedObj,
        defaultDropdownObj,
        assignOptionHasNoResults,
        setAssignOptionHasNoResults,
        activeSortField,
        setActiveSortField,
        activeSortDirection,
        setActiveSortDirection,
        isSorting,
        setIsSorting,
        nextToken,
        setNextToken,
        nextIsLoading,
        setNextIsLoading,
        filteredData,
        setFilteredData,
        toggledClearRows,
        setToggleClearRows,
        selectedRows,
        setSelectedRows,
        selectedBeaconDialogIsOpen,
        setSelectedBeaconDialogIsOpen,
        confirmationModalOpen,
        setConfirmationModalOpen,
        paginationIndex,
        setPaginationIndex,
        assetTotal,
        setAssetTotal,
        cachedFilteredDataRef,
        nextTokenCachedRef,
        assetTotalRef,
        paginationIndexRef,
        resetToCachedRefs,
      }}
    >
      {children}
    </BeaconManagementContext.Provider>
  );
};

export default BeaconManagementProvider;
